import { useCallback, useState } from 'react';
import { DeepPartial } from '../../common/assignment/interfaces';
import { buildCreateServiceProviderAssignmentRequest } from './sp-assignment-mapper';
import { ProgressStep } from './sp-assignment-progress-modal';
import { AssignmentModel } from '../../../../api-service/clients';
import { moduleClientFactory } from '../../../../api-service/module-client-factory';
import { usePostToTab } from '../../../search/hooks/usePostToTab';
import { UPDATE_ASSIGNMENT_TABLE } from '../../common/assignment/assignment-post-to-tab-topics';
import { ServiceProviderUrlBuilder } from '../utils/service-provider-url-builder';

const spReceiveCitizenClient = moduleClientFactory.createCitizenServiceProviderReceiveCitizenClient();
const RECEIVE_ASSIGNMENT_QUERY_PARAM = 'receiveAssignment';
const MAX_AUTO_RETRY_ATTEMPTS = 3;
const AUTO_RETRY_DELAY_MS = 1500;
const STEP_LABELS = ['Registrerer borger', 'Klargøring', 'Opretter bestilling'] as const;

function createPendingProgressSteps(): ProgressStep[] {
  return STEP_LABELS.map(label => ({ label, status: 'pending' }));
}

function waitForAutoRetry() {
  return new Promise<void>(resolve => {
    setTimeout(() => resolve(), AUTO_RETRY_DELAY_MS);
  });
}

function buildViewAssignmentUrl(citizenId: string, assignmentId: string, spuId?: string, companyId?: string) {
  const queryParams: Record<string, string> = {
    [RECEIVE_ASSIGNMENT_QUERY_PARAM]: 'true',
  };

  if (companyId) {
    queryParams.companyId = companyId;
  }

  return ServiceProviderUrlBuilder.buildCustomUrl(`view-assignment/${assignmentId}`, {
    citizenId,
    spuId,
    queryParams,
  });
}

interface UseCreateAssignmentAsServiceProviderInput {
  cpr?: string;
  spuId?: string;
  activityOfferId?: string;
  assignment: DeepPartial<AssignmentModel>;
  serviceProviderUnitName?: string;
}

interface ExtractedError {
  message: string;
  detail?: string;
  correlationIdentifier?: string;
}

function extractError(error: unknown): ExtractedError {
  if (typeof error !== 'object' || !error) {
    return { message: 'Ukendt fejl' };
  }

  const candidate = error as {
    response?: {
      data?: {
        messageTitle?: string;
        title?: string;
        message?: string;
        detail?: string;
        correlationIdentifier?: string;
      };
    };
    message?: string;
  };

  const data = candidate.response?.data;
  const message = data?.messageTitle || data?.title || data?.message || candidate.message || 'Ukendt fejl';

  return {
    message,
    detail: data?.detail,
    correlationIdentifier: data?.correlationIdentifier,
  };
}

export function useCreateAssignmentAsServiceProvider({ cpr, spuId, activityOfferId, assignment, serviceProviderUnitName }: UseCreateAssignmentAsServiceProviderInput) {
  const updateAssignmentTable = usePostToTab(UPDATE_ASSIGNMENT_TABLE);
  const [progressSteps, setProgressSteps] = useState<ProgressStep[]>(createPendingProgressSteps);
  const [intermediateState, setIntermediateState] = useState<{
    citizenId?: string;
    activityId?: string;
  }>({});
  const [receiveAssignmentUrl, setReceiveAssignmentUrl] = useState<string>();

  const updateStep = useCallback((index: number, update: Partial<ProgressStep>) => {
    setProgressSteps(prev => prev.map((step, i) => (i === index ? { ...step, ...update } : step)));
  }, []);

  const resetProgressSteps = useCallback(() => {
    setProgressSteps(createPendingProgressSteps());
  }, []);

  const resetFailedProgressSteps = useCallback(() => {
    setProgressSteps(prev => prev.map(step => (step.status === 'failed' ? { ...step, status: 'pending', errorMessage: undefined, errorDetail: undefined, correlationIdentifier: undefined } : step)));
  }, []);

  const executeSteps = useCallback(async () => {
    const { citizenId: storedCitizenId, activityId: storedActivityId } = intermediateState;
    async function executeAttempt(attempt: number, citizenId?: string, activityId?: string): Promise<void> {
      let currentStep = 0;
      let currentCitizenId = citizenId;
      let currentActivityId = activityId;
      try {
        if (!currentCitizenId) {
          if (!cpr) {
            throw new Error('Mangler borger-CPR i URL');
          }
          currentStep = 0;
          updateStep(0, { status: 'in-progress' });
          const citizenResponse = await spReceiveCitizenClient.ensureCitizenForAssignment({ citizenCpr: cpr });
          currentCitizenId = citizenResponse.citizenId;
          setIntermediateState(prev => ({ ...prev, citizenId: currentCitizenId }));
        }
        updateStep(0, { status: 'completed' });

        if (!currentActivityId) {
          if (!activityOfferId) {
            throw new Error('Der mangler valgt tilbud');
          }
          currentStep = 1;
          updateStep(1, { status: 'in-progress' });
          const activityResponse = await spReceiveCitizenClient.createActivityForAssignment({
            citizenId: currentCitizenId,
            offerId: activityOfferId,
            serviceProviderUnitId: spuId,
            companyId: assignment.backgroundInformation?.companyId?.value,
            serviceProviderUnitName,
          });
          currentActivityId = activityResponse.activityId;
          setIntermediateState(prev => ({ ...prev, activityId: currentActivityId }));
        }
        updateStep(1, { status: 'completed' });

        currentStep = 2;
        updateStep(2, { status: 'in-progress' });
        const assignmentRequest = buildCreateServiceProviderAssignmentRequest(assignment, currentCitizenId, currentActivityId);
        const createAssignmentResponse = await spReceiveCitizenClient.createServiceProviderAssignment(assignmentRequest);
        updateStep(2, { status: 'completed' });

        const createdAssignmentId = createAssignmentResponse.assignmentId?.value;
        if (!currentCitizenId || !createdAssignmentId) {
          throw new Error('Kunne ikke finde oprettet bestilling');
        }

        const targetUrl = buildViewAssignmentUrl(currentCitizenId, createdAssignmentId, spuId, assignment.backgroundInformation?.companyId?.value);
        setReceiveAssignmentUrl(targetUrl);
      } catch (error: unknown) {
        const { message: errorMessage, detail: errorDetail, correlationIdentifier } = extractError(error);
        const shouldAutoRetry = attempt < MAX_AUTO_RETRY_ATTEMPTS;

        if (!shouldAutoRetry) {
          updateStep(currentStep, { status: 'failed', errorMessage, errorDetail, correlationIdentifier });
          return;
        }

        updateStep(currentStep, { status: 'in-progress', errorMessage: undefined, errorDetail: undefined, correlationIdentifier: undefined });
        await waitForAutoRetry();
        await executeAttempt(attempt + 1, currentCitizenId, currentActivityId);
      }
    }

    await executeAttempt(0, storedCitizenId, storedActivityId);
  }, [activityOfferId, assignment, cpr, intermediateState, serviceProviderUnitName, spuId, updateStep]);

  const navigateToReceiveAssignment = useCallback(() => {
    if (!receiveAssignmentUrl) {
      return;
    }

    if (window.opener) {
      window.opener.location.href = receiveAssignmentUrl;
      updateAssignmentTable(window.opener);
      window.close();
      return;
    }

    window.location.assign(receiveAssignmentUrl);
  }, [receiveAssignmentUrl, updateAssignmentTable]);

  const resetIntermediateState = useCallback(() => {
    setIntermediateState({});
    setReceiveAssignmentUrl(undefined);
  }, []);

  return {
    progressSteps,
    resetProgressSteps,
    resetFailedProgressSteps,
    executeSteps,
    navigateToReceiveAssignment,
    resetIntermediateState,
  };
}
